import { AppCard } from "@/components/ui/AppCard";
import { Skeleton } from "@/components/ui/Skeleton";
import { cn } from "@/lib/utils";

export type TSectionCardSkeletonVariant = "todo" | "dealStack" | "dealNetwork" | "requestIntro";

export type TSectionCardSkeletonProps = {
  variant?: TSectionCardSkeletonVariant;
  className?: string;
};

/**
 * Ghost loading skeleton that mirrors SectionCard layout.
 * Use when loading todo, deal stack, deal network, or request-intro lists
 * so the UI looks like it's being filled in instead of a blank spinner.
 */
export function SectionCardSkeleton({ variant = "requestIntro", className }: TSectionCardSkeletonProps) {
  const showAvatar = variant === "dealNetwork" || variant === "requestIntro";
  const showActions = variant === "dealNetwork" || variant === "requestIntro";

  return (
    <AppCard className={cn("space-y-4 p-4", className)}>
      {/* Header row */}
      <div className="flex min-w-0 items-start justify-between gap-4">
        <div className="flex min-w-0 flex-1 items-start gap-4">
          {showAvatar ? <Skeleton className="h-10 w-10 flex-shrink-0 rounded-full sm:h-12 sm:w-12" /> : null}
          <div className="min-w-0 flex-1 space-y-1">
            <Skeleton className="h-4 w-32 sm:w-40" />
            {(variant === "dealStack" || variant === "dealNetwork" || variant === "requestIntro") && (
              <Skeleton className="h-3 w-24 sm:w-36" />
            )}
          </div>
        </div>
        <Skeleton className="h-11 w-11 flex-shrink-0 rounded-full sm:h-12 sm:w-12" />
      </div>

      {/* Body: paragraph lines */}
      <div className="space-y-2">
        <Skeleton className="h-3 w-full" />
        <Skeleton className="h-3 w-[95%]" />
        <Skeleton className="h-3 w-[88%]" />
        <Skeleton className="h-3 w-[70%]" />
      </div>

      {/* Optional actions */}
      {showActions ? (
        <div className="flex flex-wrap gap-2">
          <Skeleton className="h-8 w-28 rounded-lg" />
          <Skeleton className="h-8 w-20 rounded-lg" />
          <Skeleton className="h-8 w-20 rounded-lg" />
        </div>
      ) : null}

      {/* Footer: input row */}
      <div className="flex items-center gap-2 pt-0">
        <Skeleton className="h-8 w-8 flex-shrink-0 rounded-lg" />
        <Skeleton className="h-8 w-8 flex-shrink-0 rounded-lg" />
        <Skeleton className="h-8 min-w-0 flex-1 rounded-lg" />
        <Skeleton className="h-8 w-16 flex-shrink-0 rounded-lg" />
      </div>
    </AppCard>
  );
}
